import React, { createContext, useContext, useMemo } from "react";

import type { CellRendererParams } from "../components/CellRenderers/types";

/**
 * Cell view-renderer override hook.
 *
 * Receives the same params the cell renderer receives, the resolved
 * `defaultText` (the string the renderer would otherwise have shown — i.e.
 * the result of any `CellFormatOverride`, then `valueFormatted`, then
 * `value`, then the missing/null sentinel), and the renderer's own richer
 * `fallbackNode` when it has one (e.g. a benefit with a dedicated
 * cell-renderer component, as opposed to plain text).
 *
 * Returns either a React node to render in place of the default text span,
 * or `undefined` to fall through to the caller's own default rendering —
 * `fallbackNode` when present, otherwise the plain text span. An override
 * with nothing to show for this cell (e.g. no saved custom text) should
 * return `undefined` when `fallbackNode` is provided, so a benefit with its
 * own richer renderer keeps showing it instead of being replaced by plain
 * `defaultText`.
 *
 * This is the right extension point when the consumer needs to render
 * arbitrary JSX inside a cell — e.g. a popover trigger that lets the user
 * edit a typed-amount pair (`{type, amount}`) as a single inline control —
 * without losing the library-owned status corners, icons, and background
 * tints painted around it.
 *
 * For pure text replacement, use `CellFormatOverride` instead.
 */
export type CellViewRendererOverride = (
  params: CellRendererParams,
  defaultText: string,
  fallbackNode?: React.ReactNode,
) => React.ReactNode | undefined;

const CellViewRendererOverrideContext =
  createContext<CellViewRendererOverride | null>(null);

interface CellViewRendererOverrideProviderProps {
  override: CellViewRendererOverride;
  children: React.ReactNode;
}

export const CellViewRendererOverrideProvider = ({
  override,
  children,
}: CellViewRendererOverrideProviderProps): React.JSX.Element => {
  const value = useMemo(() => override, [override]);
  return (
    <CellViewRendererOverrideContext.Provider value={value}>
      {children}
    </CellViewRendererOverrideContext.Provider>
  );
};

/** Returns the registered view-renderer override or null if no provider is mounted. */
export const useCellViewRendererOverride =
  (): CellViewRendererOverride | null =>
    useContext(CellViewRendererOverrideContext);
